import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Save, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input, Textarea } from '@/components/ui/Input'
import { formatCurrency } from '@/utils/format-currency'
import type { BatchPaymentForm } from './usePartnerPayments'

interface PartnerBatchPaymentModalProps {
  open: boolean
  busy: boolean
  total: number
  commissionsCount: number
  partnersCount: number
  onClose: () => void
  onConfirm: (form: BatchPaymentForm) => void
}

const emptyForm = (): BatchPaymentForm => ({
  metodo: 'Pix',
  dataPagamento: new Date().toISOString().slice(0, 10),
  notas: '',
})

export function PartnerBatchPaymentModal({
  open,
  busy,
  total,
  commissionsCount,
  partnersCount,
  onClose,
  onConfirm,
}: PartnerBatchPaymentModalProps) {
  const [form, setForm] = useState<BatchPaymentForm>(emptyForm)

  useEffect(() => {
    if (open) setForm(emptyForm())
  }, [open])

  if (!open) return null

  return createPortal(
    <div className='fixed inset-0 z-[60] flex items-center justify-center p-4'>
      <div className='absolute inset-0 bg-black/60 backdrop-blur-[2px]' onClick={onClose} />
      <div className='relative z-10 w-full max-w-md rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-2xl'>
        <div className='mb-4 flex items-center justify-between'>
          <h3 className='font-semibold text-[var(--font-color-h3)]' style={{ fontSize: 'var(--font-size-h3)' }}>
            Registrar pagamento
          </h3>
          <button onClick={onClose} className='rounded-md p-1.5 text-[var(--text-muted)] transition-colors hover:bg-[var(--glass-bg)] hover:text-[var(--text-primary)]'>
            <X size={16} />
          </button>
        </div>

        <div className='mb-4 rounded-lg border border-[var(--border)] bg-[var(--bg-main)] p-3'>
          <p className='text-xs text-[var(--font-color-muted)]'>
            {commissionsCount} comissão(ões) de {partnersCount} parceiro(s)
          </p>
          <p className='mt-1 text-xl font-bold text-emerald-500'>{formatCurrency(total)}</p>
          {partnersCount > 1 && (
            <p className='mt-1 text-[11px] text-[var(--font-color-muted)]'>
              Será registrado um pagamento separado para cada parceiro.
            </p>
          )}
        </div>

        <div className='space-y-3'>
          <div className='flex flex-col gap-1.5'>
            <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
              Método
            </label>
            <select
              value={form.metodo}
              onChange={(e) => setForm({ ...form, metodo: e.target.value })}
              className='rounded-md border border-[var(--border)] bg-[var(--bg-main)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-primary)]'
            >
              <option value='Pix'>Pix</option>
              <option value='TED'>TED</option>
              <option value='Boleto'>Boleto (Inverso)</option>
            </select>
          </div>
          <Input
            label='Data do pagamento'
            type='date'
            value={form.dataPagamento}
            onChange={(e) => setForm({ ...form, dataPagamento: e.target.value })}
          />
          <Textarea
            label='Notas / referência'
            value={form.notas}
            onChange={(e) => setForm({ ...form, notas: e.target.value })}
            placeholder='Ex: Repasse de outubro'
            rows={3}
          />
        </div>

        <div className='mt-4 flex items-center justify-end gap-2'>
          <Button variant='ghost' size='sm' onClick={onClose}>Cancelar</Button>
          <Button variant='primary' size='sm' icon={<Save size={13} />} loading={busy} onClick={() => onConfirm(form)}>
            Confirmar pagamento
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
